import type { ParseKeys, TFunction } from 'i18next'

import { describeProblem, fieldErrors } from '@/lib/problems'

/* A refused write, as the sentence the reader gets.
 *
 * `problems.ts` maps a problem document to KEYS and stops there, deliberately —
 * it is the transport layer's reading of the wire and has no business holding a
 * `t`. This is the layer above it: the one that turns those keys, plus the one
 * refusal that carries facts inside its field errors, into words.
 *
 * It lives in `lib/` because three features raise documents through the same
 * pipeline — the quote's offer, the case's invoice or debit note, the Money
 * page's client invoice — and every one of them can be handed the same FR-64
 * refusal. It used to live in `features/quotes/quoteText.ts`, which was true
 * while the offer sheet was the only surface that recognised it and became a
 * lie the moment a second one had to: a case document is not an offer, and a
 * feature module is not somewhere two other features should be importing their
 * error wording from.
 */

/* The FR-64 branding refusal, from the wire to a sentence.
 *
 * `platform/docs` reports it as one field — `issuingEntity` — carrying the bare
 * sentinel followed by one qualified code per missing field:
 * `[branding-incomplete, branding-incomplete:legal-footer,
 * branding-incomplete:bank-details]`. The sentinel leads and is what this
 * client matches on; the qualified codes are what it names. All three server
 * modules that can raise it — `internal/quote`, `internal/document`,
 * `internal/casefile` — answer that same shape through
 * `docs.ErrBrandingIncomplete.Codes`, so one reader here is enough.
 *
 * The field names arrive as MACHINE KEYS and the words stay here (AD-27). A
 * map rather than a sentence assembled server-side, because a server that
 * wrote «юридический футер» would be writing interface copy in a language it
 * does not know the reader is using — and because a key an older client cannot
 * word degrades to the unnamed sentence rather than to English inside Russian.
 *
 * `legal-footer` → `legalFooter` is the only transformation: the keys are
 * lower-case words joined by hyphens (docs.Branding.Missing pins that), and the
 * catalogue is camelCase like every other key in it. Written out rather than
 * computed so that a key with no words is a MISS here rather than a lookup of a
 * catalogue key that does not exist — the miss is what the drop below needs. */
const BRANDING_FIELD_KEYS: Record<string, ParseKeys> = {
  name: 'errors.branding.field.name',
  logo: 'errors.branding.field.logo',
  'legal-footer': 'errors.branding.field.legalFooter',
  'bank-details': 'errors.branding.field.bankDetails',
}

const BRANDING_CODE = 'branding-incomplete'

/** The fields a branding refusal named, or null when it is not that refusal.
 *
 * An empty array and null are different answers on purpose: the empty array is
 * "the entity's branding is incomplete and the server said no more than that",
 * which still belongs in Settings; null is "this was some other failure", which
 * must fall through to the generic mapping rather than acquire a cause the
 * client invented. */
export function brandingRefusalFields(error: unknown): string[] | null {
  const codes = fieldErrors(error)['issuingEntity']
  if (!codes?.includes(BRANDING_CODE)) return null
  return codes
    .filter((code) => code.startsWith(`${BRANDING_CODE}:`))
    .map((code) => code.slice(BRANDING_CODE.length + 1))
}

/** The branding refusal as one sentence, naming the fields and the screen.
 *
 * A field this catalogue has no words for is DROPPED rather than printed raw: a
 * server that grows a branding field before this client does would otherwise
 * show `vat-number` to a user. Dropping every one of them lands on the unnamed
 * sentence, which still says where to go — a worse message than the list, and a
 * far better one than a machine key in the middle of a Russian paragraph. */
export function brandingRefusalMessage(fields: string[], t: TFunction): string {
  const named = fields
    .map((field) => BRANDING_FIELD_KEYS[field])
    .filter((key): key is ParseKeys => key !== undefined)
    .map((key) => t(key))

  if (named.length === 0) return t('errors.branding.incomplete')
  return t('errors.branding.incompleteFields', {
    // Comma-joined here rather than in the catalogue: both languages punctuate
    // a list of nouns the same way, and a sentence holding the join could not
    // be given a different one per language anyway.
    fields: named.join(', '),
  })
}

/** A refused write as one line, in the viewer's language.
 *
 * The one call every sheet that raises or sends a document should make. Through
 * `describeProblem` rather than the problem's own title or detail (AD-22,
 * AD-27): those are written server-side in English for a log, and a Russian
 * user meeting a rejected save is the last person who should be handed a second
 * language.
 *
 * The branding case is checked FIRST because the generic mapping cannot word
 * it. It arrives as a validation problem on `issuingEntity` — a field none of
 * these sheets renders, so the field-level plumbing has nowhere to put it, and
 * "the highlighted fields could not be accepted" highlights nothing. The fix
 * lives in Settings → Entities, and only this sentence can send the reader
 * there. */
export function refusalMessage(error: unknown, t: TFunction): string {
  const missing = brandingRefusalFields(error)
  if (missing !== null) return brandingRefusalMessage(missing, t)
  return t(describeProblem(error).detailKey)
}
